import React, { useState, useEffect } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { confirmAlert } from 'react-confirm-alert';
import { useForm } from 'react-hook-form';

import useMasterGroupsHook from '../../../api/masterGroups';
import useSimpleDataHook   from '../../../api/simpleData';       // NEW
import { ViewMasterGroups } from '../../../components/admin/master-groups/ViewMasterGroups';
import { FormMasterGroups } from '../../../components/admin/master-groups/FormMasterGroups';

import { useTranslation } from 'react-i18next';
import { Spinner, Pagination, Message, Confirm, Search } from '../../../components';
import DefaultGroupIcon from '../../../media/vending-machine.svg';

const MasterGroups = () => {
    const { t } = useTranslation();

    /* ---------------- local state ---------------- */
    const [page, setPage] = useState(1);
    const [q, setQ]       = useState('');
    const [edit, setEdit] = useState(false);
    const [groupId, setGroupId] = useState(null);

    const [selectedUsers,    setSelectedUsers]    = useState([]);
    const [selectedMachines, setSelectedMachines] = useState([]);

    /* ------------- API hooks ------------- */
    const {
        getMasterGroups,
        postMasterGroup,
        updateMasterGroup,
        deleteMasterGroup
    } = useMasterGroupsHook({ page, q });

    // lightweight lists
    const { getSimpleUsers, getSimpleMachines } = useSimpleDataHook({ limit: 100000, page: 1 });
    const { data: dataUsers    } = getSimpleUsers;
    const { data: dataMachines } = getSimpleMachines;

    /* ------------- RHF ------------- */
    const {
        register,
        handleSubmit,
        setValue,
        reset,
        formState: { errors }
    } = useForm({
        defaultValues: {
            group_name : '',
            description: '',
            users      : '[]',
            machines   : '[]'
        }
    });

    /* ------------- query states ------------- */
    const { data, isLoading, isError, error, refetch } = getMasterGroups;
    const { mutateAsync: mutateCreate,  isLoading: isCreating,  isSuccess: isSuccessCreate  } = postMasterGroup;
    const { mutateAsync: mutateUpdate,  isLoading: isUpdating,  isSuccess: isSuccessUpdate  } = updateMasterGroup;
    const { mutateAsync: mutateDelete,  isLoading: isDeleting }  = deleteMasterGroup;

    /* ---------- helpers ---------- */
    const formCleanHandler = () => {
        reset();
        setEdit(false);
        setGroupId(null);
        setSelectedUsers([]);
        setSelectedMachines([]);
    };

    /* ---------- side‑effects ---------- */
    useEffect(() => { refetch(); }, [page, q, refetch]);

    useEffect(() => {
        if (isSuccessCreate || isSuccessUpdate) {
            document.getElementById('masterGroupModalClose')?.click();
        }
    }, [isSuccessCreate, isSuccessUpdate]);

    /* ---------- UI callbacks ---------- */
    const searchHandler  = (e) => { e.preventDefault(); setPage(1); refetch(); };
    const deleteHandler  = (id) => confirmAlert(Confirm(() => mutateDelete(id)));

    const submitHandler = async (formData) => {
          const payload = {
                ...formData,
                users   : Array.isArray(formData.users)    ? formData.users    : JSON.parse(formData.users || '[]'),
                machines: Array.isArray(formData.machines) ? formData.machines : JSON.parse(formData.machines || '[]')
              };
        if (edit) await mutateUpdate({ _id: groupId, ...payload });
        else      await mutateCreate(payload);
    };

    const editHandler = (g) => {
        setEdit(true); setGroupId(g._id);
        setValue('group_name',  g.group_name);
        setValue('description', g.description || '');

        // preload users
        const usr = (g.users || []).map(u => ({ value: u._id, label: u.name }));
        setSelectedUsers(usr);
        setValue('users', JSON.stringify(usr.map(v => v.value)));

        // preload machines
        const mcs = (g.machines || []).map(m => ({
            value: m._id,
            label: `${m.machine_name} (${m.kiosk_external_id})`
        }));
        setSelectedMachines(mcs);
        setValue('machines', JSON.stringify(mcs.map(v => v.value)));
    };

    /* ---------------- render ---------------- */
    return (
        <>
            <HelmetProvider>
                <Helmet>
                    <title>Master Groups</title>
                    <meta property="og:title" content="Master Groups" />
                </Helmet>
            </HelmetProvider>

            <div className="view-list-top-bar">
                <h3 className="fw-light text-muted text-center mb-3">
                    {t('Groups List')} <sup className="fs-6">[{data?.total || 0}]</sup>
                    <img src={DefaultGroupIcon} alt="Groups" className="admin-list-title-icon ms-2" />
                </h3>

                <div className="d-flex align-items-center justify-content-between mb-2">
                    <button
                        className="btn add-new-entry-button"
                        data-bs-toggle="modal"
                        data-bs-target="#masterGroupModal"
                        onClick={formCleanHandler}
                    >
                        {t('Add New Group')}
                    </button>
                    <Pagination data={data} setPage={setPage} />
                </div>

                <div className="col-auto full-search">
                    <Search
                        placeholder={t('Search by Group Name')}
                        setQ={setQ}
                        q={q}
                        searchHandler={searchHandler}
                    />
                </div>
            </div>

            {isLoading ? (
                <Spinner />
            ) : isError ? (
                <Message variant="danger">{error?.message || 'Unexpected error'}</Message>
            ) : (
                <ViewMasterGroups
                    data={data}
                    editHandler={editHandler}
                    deleteHandler={deleteHandler}
                    isLoadingDelete={isDeleting}
                    useTranslation={useTranslation}
                />
            )}

            <div className="pagination-bottom-block">
                <Pagination data={data} setPage={setPage} />
            </div>

            {/* modal */}
            <FormMasterGroups
                edit={edit}
                formCleanHandler={formCleanHandler}
                isLoading={isLoading}
                isError={isError}
                isErrorUpdate={false}
                errors={errors}
                isLoadingUpdate={isUpdating}
                isLoadingPost={isCreating}
                register={register}
                handleSubmit={handleSubmit}
                submitHandler={submitHandler}
                error={error}
                useTranslation={useTranslation}
                /* new props */
                dataUsers={dataUsers}
                dataMachines={dataMachines}
                selectedUsers={selectedUsers}
                setSelectedUsers={setSelectedUsers}
                selectedMachines={selectedMachines}
                setSelectedMachines={setSelectedMachines}
                setValue={setValue}
            />
        </>
    );
};

export default MasterGroups;
